import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Slider,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayArrowIcon,
  History as HistoryIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { vcpService, VCPPattern, BacktestSummary, BacktestTrade, BacktestResult, VCPCriteria } from '../../services/vcpService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const VCPScanner: React.FC = () => {
  // State for scanner
  const [candidates, setCandidates] = useState<VCPPattern[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [minQuality, setMinQuality] = useState(40);
  const [stage2Only, setStage2Only] = useState(false);
  const [nearBreakoutOnly, setNearBreakoutOnly] = useState(false);
  const [minRS, setMinRS] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Backtest state
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestResults, setBacktestResults] = useState<BacktestResult[]>([]);
  const [currentBacktest, setCurrentBacktest] = useState<{
    summary: BacktestSummary;
    sampleTrades: BacktestTrade[];
  } | null>(null);
  const [backtestDialogOpen, setBacktestDialogOpen] = useState(false);
  const [backtestStartDate, setBacktestStartDate] = useState('2024-01-01');
  const [backtestEndDate, setBacktestEndDate] = useState('2025-01-01');
  const [backtestMaxStocks, setBacktestMaxStocks] = useState(100);
  
  // Criteria info
  const [criteria, setCriteria] = useState<VCPCriteria | null>(null);
  const [showCriteria, setShowCriteria] = useState(false);
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadCriteria();
  }, []);

  const loadCriteria = async () => {
    try {
      const response = await vcpService.getVCPCriteria();
      if (response.success) {
        setCriteria(response.criteria);
      }
    } catch (err) {
      console.error('Error loading VCP criteria:', err);
    }
  };

  const scanForPatterns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vcpService.scanForVCP({
        min_quality: minQuality,
        stage2_only: stage2Only,
        near_breakout_only: nearBreakoutOnly,
        min_rs: minRS,
        limit: 100,
      });
      
      if (response.success) {
        setCandidates(response.candidates);
        toast.success(`Found ${response.count} VCP candidates`);
      } else {
        setError(response.error || 'Failed to scan for patterns');
      }
    } catch (err: any) {
      setError(err.message || 'Error scanning for VCP patterns');
      toast.error('Failed to scan for VCP patterns');
    } finally {
      setLoading(false);
    }
  };

  const runBacktest = async () => {
    setBacktestLoading(true);
    try {
      const response = await vcpService.runBacktest({
        start_date: backtestStartDate,
        end_date: backtestEndDate,
        min_quality: minQuality,
        max_stocks: backtestMaxStocks,
      });
      
      if (response.success && response.summary) {
        setCurrentBacktest({
          summary: response.summary,
          sampleTrades: response.sample_trades || [],
        });
        toast.success('Backtest completed successfully');
        setBacktestDialogOpen(false);
        loadBacktestResults();
      } else {
        toast.error(response.error || 'Backtest failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Error running backtest');
    } finally {
      setBacktestLoading(false);
    }
  };

  const loadBacktestResults = async () => {
    try {
      const response = await vcpService.getBacktestResults(10);
      if (response.success) {
        setBacktestResults(response.results);
      }
    } catch (err) {
      console.error('Error loading backtest results:', err);
    }
  };

  const toggleRowExpanded = (symbol: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(symbol)) {
      newExpanded.delete(symbol);
    } else {
      newExpanded.add(symbol);
    }
    setExpandedRows(newExpanded);
  };

  const getQualityColor = (score: number) => {
    if (score >= 70) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            VCP Scanner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Volatility Contraction Pattern - Mark Minervini's breakout strategy
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Learn about VCP">
            <IconButton onClick={() => setShowCriteria(!showCriteria)}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* VCP Criteria Info */}
      <Collapse in={showCriteria}>
        <Card sx={{ mb: 1, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {criteria?.name || 'VCP Pattern Criteria'}
            </Typography>
            <Typography variant="body2" paragraph>
              {criteria?.description}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Key Elements:</Typography>
                {criteria?.key_elements.map((element, idx) => (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">{element.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{element.description}</Typography>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Entry Rules:</Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {criteria?.entry_rules.map((rule, idx) => (
                    <li key={idx}><Typography variant="body2">{rule}</Typography></li>
                  ))}
                </ul>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Success Factors:</Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {criteria?.success_factors.map((factor, idx) => (
                    <li key={idx}><Typography variant="body2">{factor}</Typography></li>
                  ))}
                </ul>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Stock Selection Strategy */}
      <Card sx={{ mb: 1, bgcolor: '#f5f5f5' }}>
        <CardContent sx={{ textAlign: 'left' }}>
          <Typography variant="h6" gutterBottom color="primary">
            Strategy for Picking VCP Stocks
          </Typography>
          
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                High-Quality VCP Setup Criteria:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <li><Typography variant="body2"><strong>VCP Score ≥ 70</strong> - Excellent pattern quality</Typography></li>
                <li><Typography variant="body2"><strong>Stage 2 = Yes</strong> - Stock in confirmed uptrend</Typography></li>
                <li><Typography variant="body2"><strong>RS Rating ≥ 80</strong> - Strong relative strength vs market</Typography></li>
                <li><Typography variant="body2"><strong>3-5 Contractions</strong> - Ideal number of pullbacks</Typography></li>
                <li><Typography variant="body2"><strong>Volume Dry-Up ≥ 60</strong> - Good volume contraction</Typography></li>
                <li><Typography variant="body2"><strong>Near Breakout = Yes</strong> - Within 3% of pivot point</Typography></li>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                How to Use the Analysis Page:
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 2 }}>
                <li><Typography variant="body2">Go to <strong>/analysis</strong> page</Typography></li>
                <li><Typography variant="body2">Add VCP columns: VCPScore, VCPStage2, VCPRSRating, VCPNearBreakout</Typography></li>
                <li><Typography variant="body2">Sort by <strong>VCPScore</strong> descending</Typography></li>
                <li><Typography variant="body2">Filter for VCPScore ≥ 50 and VCPStage2 = ✓</Typography></li>
                <li><Typography variant="body2">Focus on stocks with RS Rating ≥ 70</Typography></li>
              </Box>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  VCP analysis runs automatically during the nightly technical analysis job (16:00 IST).
                  Pre-computed VCP scores are available in the Analysis page.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Risk Management:
            </Typography>
            <Typography variant="body2">
              • Set stop loss 7-8% below entry price • Buy on breakout above pivot with volume confirmation 
              • Position size: Risk no more than 1-2% of portfolio per trade • Best results when market indices are in uptrend
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}>
        <Tab label="Scanner" icon={<FilterIcon />} iconPosition="start" />
        <Tab label="Backtest" icon={<HistoryIcon />} iconPosition="start" />
      </Tabs>

      {/* Scanner Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Card sx={{ mb: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Filters</Typography>
              <IconButton onClick={() => setShowFilters(!showFilters)}>
                {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={showFilters}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>Minimum Quality Score: {minQuality}</Typography>
                  <Slider
                    value={minQuality}
                    onChange={(_, v) => setMinQuality(v as number)}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>Minimum RS Rating: {minRS}</Typography>
                  <Slider
                    value={minRS}
                    onChange={(_, v) => setMinRS(v as number)}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={stage2Only}
                        onChange={(e) => setStage2Only(e.target.checked)}
                      />
                    }
                    label="Stage 2 Uptrend Only"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={nearBreakoutOnly}
                        onChange={(e) => setNearBreakoutOnly(e.target.checked)}
                      />
                    }
                    label="Near Breakout Only"
                  />
                </Grid>
              </Grid>
            </Collapse>
            
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={scanForPatterns}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              >
                {loading ? 'Scanning...' : 'Scan for VCP Patterns'}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Note: VCP analysis is also run automatically during the nightly technical analysis job.
                Check the Analysis page for pre-computed VCP scores.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {/* Results Table */}
        {candidates.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                VCP Candidates ({candidates.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell align="right">Quality</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Pivot</TableCell>
                      <TableCell align="right">Distance</TableCell>
                      <TableCell align="center">Stage 2</TableCell>
                      <TableCell align="right">RS Rating</TableCell>
                      <TableCell align="right">Contractions</TableCell>
                      <TableCell align="right">Vol Dry-Up</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.map((pattern) => (
                      <React.Fragment key={pattern.symbol}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => toggleRowExpanded(pattern.symbol)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expandedRows.has(pattern.symbol) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography fontWeight="bold">{pattern.symbol}</Typography>
                              {pattern.is_near_breakout && (
                                <Chip label="Near Breakout" size="small" color="warning" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={pattern.pattern_quality_score.toFixed(0)}
                              size="small"
                              color={getQualityColor(pattern.pattern_quality_score)}
                            />
                          </TableCell>
                          <TableCell align="right">₹{pattern.current_price.toFixed(2)}</TableCell>
                          <TableCell align="right">₹{pattern.pivot_price.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              color={pattern.distance_from_pivot_pct > 0 ? 'success.main' : 'error.main'}
                            >
                              {formatPercent(pattern.distance_from_pivot_pct)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {pattern.in_stage2_uptrend ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <CancelIcon color="error" fontSize="small" />
                            )}
                          </TableCell>
                          <TableCell align="right">{pattern.rs_rating.toFixed(0)}</TableCell>
                          <TableCell align="right">{pattern.num_contractions}</TableCell>
                          <TableCell align="right">{pattern.volume_dry_up_score.toFixed(0)}%</TableCell>
                        </TableRow>
                        
                        {/* Expanded Row */}
                        <TableRow>
                          <TableCell colSpan={10} sx={{ py: 0 }}>
                            <Collapse in={expandedRows.has(pattern.symbol)}>
                              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" gutterBottom>Moving Averages</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip
                                        label="50 MA"
                                        size="small"
                                        color={pattern.price_above_50ma ? 'success' : 'default'}
                                        variant={pattern.price_above_50ma ? 'filled' : 'outlined'}
                                      />
                                      <Chip
                                        label="150 MA"
                                        size="small"
                                        color={pattern.price_above_150ma ? 'success' : 'default'}
                                        variant={pattern.price_above_150ma ? 'filled' : 'outlined'}
                                      />
                                      <Chip
                                        label="200 MA"
                                        size="small"
                                        color={pattern.price_above_200ma ? 'success' : 'default'}
                                        variant={pattern.price_above_200ma ? 'filled' : 'outlined'}
                                      />
                                      <Chip
                                        label="200 MA ↑"
                                        size="small"
                                        color={pattern.ma_200_slope_positive ? 'success' : 'default'}
                                        variant={pattern.ma_200_slope_positive ? 'filled' : 'outlined'}
                                      />
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" gutterBottom>Contractions</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                      {pattern.contraction_percentages.map((pct, idx) => (
                                        <React.Fragment key={idx}>
                                          <Chip label={`${pct.toFixed(1)}%`} size="small" />
                                          {idx < pattern.contraction_percentages.length - 1 && (
                                            <TrendingDownIcon fontSize="small" color="success" />
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" gutterBottom>Breakout Probability</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinearProgress
                                        variant="determinate"
                                        value={pattern.breakout_probability}
                                        sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                                        color={pattern.breakout_probability >= 60 ? 'success' : 'warning'}
                                      />
                                      <Typography variant="body2">
                                        {pattern.breakout_probability.toFixed(0)}%
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {!loading && candidates.length === 0 && (
          <Card>
            <CardContent>
              <Typography color="text.secondary" align="center">
                Click "Scan for VCP Patterns" to find stocks with volatility contraction patterns
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* Backtest Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card sx={{ mb: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Run Backtest</Typography>
              <Button
                variant="contained"
                onClick={() => setBacktestDialogOpen(true)}
                startIcon={<PlayArrowIcon />}
              >
                New Backtest
              </Button>
            </Box>
            
            {currentBacktest && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Latest Backtest Results</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">Total Patterns</Typography>
                        <Typography variant="h5">{currentBacktest.summary.total_patterns}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">Success Rate</Typography>
                        <Typography variant="h5" color="success.main">
                          {currentBacktest.summary.success_rate.toFixed(1)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">Avg Gain</Typography>
                        <Typography variant="h5" color="success.main">
                          +{currentBacktest.summary.avg_gain_on_success.toFixed(1)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">Expectancy</Typography>
                        <Typography variant="h5" color={currentBacktest.summary.expectancy >= 0 ? 'success.main' : 'error.main'}>
                          {currentBacktest.summary.expectancy.toFixed(2)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                
                {/* Sample Trades */}
                {currentBacktest.sampleTrades.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Sample Trades</Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Quality</TableCell>
                            <TableCell align="center">Breakout</TableCell>
                            <TableCell align="right">Return</TableCell>
                            <TableCell align="right">Max Gain</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {currentBacktest.sampleTrades.map((trade, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{trade.symbol}</TableCell>
                              <TableCell>{trade.detection_date}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={trade.pattern_quality.toFixed(0)}
                                  size="small"
                                  color={getQualityColor(trade.pattern_quality)}
                                />
                              </TableCell>
                              <TableCell align="center">
                                {trade.breakout_occurred ? (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                ) : (
                                  <CancelIcon color="error" fontSize="small" />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  color={trade.final_return_pct >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatPercent(trade.final_return_pct)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography color="success.main">
                                  +{trade.max_gain_pct.toFixed(1)}%
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Historical Backtest Results */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Historical Backtests</Typography>
              <Button onClick={loadBacktestResults} startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Box>
            
            {backtestResults.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Patterns</TableCell>
                      <TableCell align="right">Success Rate</TableCell>
                      <TableCell align="right">Avg Gain</TableCell>
                      <TableCell align="right">Expectancy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backtestResults.map((result) => (
                      <TableRow key={result.id}>
                        <TableCell>{result.backtest_date}</TableCell>
                        <TableCell>{result.start_date} to {result.end_date}</TableCell>
                        <TableCell align="right">{result.total_patterns_found}</TableCell>
                        <TableCell align="right">{result.success_rate.toFixed(1)}%</TableCell>
                        <TableCell align="right">
                          {result.avg_gain_on_success ? `+${result.avg_gain_on_success.toFixed(1)}%` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {result.expectancy ? `${result.expectancy.toFixed(2)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center">
                No backtest results yet. Run a backtest to see historical performance.
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Backtest Dialog */}
      <Dialog open={backtestDialogOpen} onClose={() => setBacktestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run VCP Backtest</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={backtestStartDate}
                  onChange={(e) => setBacktestStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="End Date"
                  type="date"
                  value={backtestEndDate}
                  onChange={(e) => setBacktestEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom>Minimum Quality Score: {minQuality}</Typography>
                <Slider
                  value={minQuality}
                  onChange={(_, v) => setMinQuality(v as number)}
                  min={0}
                  max={100}
                  step={5}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Max Stocks to Test"
                  type="number"
                  value={backtestMaxStocks}
                  onChange={(e) => setBacktestMaxStocks(parseInt(e.target.value) || 100)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBacktestDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runBacktest}
            disabled={backtestLoading}
            startIcon={backtestLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          >
            {backtestLoading ? 'Running...' : 'Run Backtest'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VCPScanner;
